import React, { useState, useEffect } from 'react';
import { Phone, Mail, MapPin, MessageCircle } from 'lucide-react';
import { api } from '../lib/api';
import type { SiteSettings } from '../types';
import { Alert } from '../components/ui';
import ImageHero from '../components/ImageHero';

const whatsappHref = (w: string) => (w.startsWith('http') ? w : `https://wa.me/${w.replace(/[^+\d]/g, '')}`);

export default function Contact() {
  const [settings, setSettings] = useState<SiteSettings>({});
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const whatsapp = settings.whatsapp ? whatsappHref(settings.whatsapp) : null;

  useEffect(() => {
    api.get<SiteSettings>('/settings').then(setSettings).catch(() => {});
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage('');
    setError('');
    setLoading(true);
    try {
      const data = await api.post<{ message: string }>('/dashboard/contact', form);
      setMessage(data.message || 'Your message has been sent.');
      setForm({ name: '', email: '', phone: '', message: '' });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send message');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {/* Hero Section */}
      <ImageHero height="h-screen" imageKey="hero_image_contact" fallbackClass="bg-contact-hero">
        <div className="max-w-7xl mx-auto px-4 h-full flex items-start pt-[33vh]">
          <div className="text-white max-w-3xl">
            <p className="uppercase tracking-[0.35em] text-primary text-xs md:text-sm font-semibold mb-4">Contact Us</p>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-bold leading-tight mb-4 capitalize">
              Get In Touch
            </h1>
            <p className="uppercase tracking-[0.35em] text-white text-xs md:text-sm font-semibold">
              We Would Love To Hear From You
            </p>
          </div>
        </div>
      </ImageHero>

      {/* Contact Form Section */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <div className="grid md:grid-cols-2 gap-12">
          <div>
            <p className="uppercase tracking-[0.3em] text-obsidian text-xs font-semibold mb-3">Reach Us</p>
            <h2 className="text-3xl md:text-4xl font-semibold text-obsidian mb-6 capitalize">Let's Connect</h2>
            <p className="text-charcoal mb-8 leading-relaxed">
              We'd love to hear from you. Please fill out the form below or use our contact information to reach us.
            </p>

            <div className="space-y-6">
              {settings.phone && (
                <div className="flex items-center p-4 bg-white rounded-xl shadow-sm hover:bg-primary/10 transition-colors border border-primary/10">
                  <Phone className="w-6 h-6 text-primary mr-4" />
                  <a href={`tel:${settings.phone.replace(/[^+\d]/g, '')}`} className="text-charcoal hover:text-primary">
                    {settings.phone}
                  </a>
                </div>
              )}
              {settings.email && (
                <div className="flex items-center p-4 bg-white rounded-xl shadow-sm hover:bg-primary/10 transition-colors border border-primary/10">
                  <Mail className="w-6 h-6 text-primary mr-4" />
                  <a href={`mailto:${settings.email}`} className="text-charcoal hover:text-primary lowercase">
                    {settings.email}
                  </a>
                </div>
              )}
              {settings.address && (
                <div className="flex items-center p-4 bg-white rounded-xl shadow-sm hover:bg-primary/10 transition-colors border border-primary/10">
                  <MapPin className="w-6 h-6 text-primary mr-4" />
                  <a
                    href="https://maps.google.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-charcoal hover:text-primary"
                  >
                    {settings.address}
                  </a>
                </div>
              )}
              {whatsapp && (
                <a
                  href={whatsapp}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center p-4 bg-obsidian rounded-xl hover:bg-charcoal transition-colors group border border-primary/20"
                >
                  <MessageCircle className="w-6 h-6 text-primary mr-4" />
                  <span className="text-white group-hover:text-primary">Chat with us on WhatsApp</span>
                </a>
              )}
            </div>
          </div>

          <div className="bg-white p-8 rounded-xl shadow-lg border-t-4 border-primary">
            {message && <Alert type="success">{message}</Alert>}
            {error && <Alert>{error}</Alert>}
            <form className="space-y-6" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-charcoal mb-1">
                  Full Name
                </label>
                <input
                  type="text"
                  id="name"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-charcoal mb-1">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-charcoal mb-1">
                  Phone
                </label>
                <input
                  type="tel"
                  id="phone"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-charcoal mb-1">
                  Message
                </label>
                <textarea
                  id="message"
                  rows={4}
                  required
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                ></textarea>
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-primary text-black py-2 font-semibold hover:bg-primary-light transition-colors disabled:opacity-50"
              >
                {loading ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Location Map */}
      {settings.address && (
        <section className="max-w-7xl mx-auto px-4 py-16">
          <div className="text-center mb-8">
            <p className="uppercase tracking-[0.3em] text-obsidian text-sm font-semibold mb-3">Find Us</p>
            <h2 className="text-3xl md:text-4xl font-semibold text-obsidian mb-3 capitalize">Our Location</h2>
            <p className="text-charcoal max-w-2xl mx-auto">{settings.address}</p>
          </div>
          <div className="rounded-xl overflow-hidden shadow-lg border-t-4 border-primary">
            <iframe
              src={`https://www.google.com/maps?q=${encodeURIComponent(settings.address)}&output=embed`}
              title={`Map showing ${settings.address}`}
              loading="lazy"
              allowFullScreen
              referrerPolicy="no-referrer-when-downgrade"
              className="w-full h-[350px] md:h-[450px] border-0"
            />
          </div>
        </section>
      )}
    </div>
  );
}
